"use client";

import parse, { domToReact, Element, DOMNode } from "html-react-parser";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { InlineInfoBox } from "staysure-component-library";

import { ApiError } from "@/app/common-types/api-error.types";
import { getQuoteReviewSummaryEditSection } from "@/app/common-utils/edit-quote-review-summary-flow.utils";
import { isContactPageShiftFlowExperimentOn } from "@/app/components/contact-details/utils/contact-details-page.util";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import withApplication from "@/components/with-application";
import withMedicalFlow from "@/components/with-medical-flow-v2";
import withUndiagnosedSymptoms, {
  MedicalConfirmationFlowEnum,
} from "@/components/with-undiagnosed-symptoms";
import {
  QuestionMedicalConditions,
  QuestionMedicalConfirmation,
  QuestionMedicalTreatments,
  MedicalEdit,
  TravellerEdit,
  OrganizerEdit,
  Individual,
  RenewalPage,
  QuestionMedicalEligibility,
  OptimizelyExperimentsEnum,
  Quote,
} from "@/config/app-constant";
import { Routes } from "@/config/page-view-constants";
import {
  APPLICATION,
  BRAND_STAYSURE,
  PRODUCT,
  VARIANT_FLOW,
} from "@/constants";
import useSaveGateKeeperAnswers from "@/hooks/api/medical/use-save-gateKeeper-answers";
import { usePartnerMessage } from "@/hooks/use-partner-message";
import { useGatekeeperQuestions } from "@/providers/gatekeeper-questions-provider";
import { useAppDispatch, useAppSelector } from "@/store/hook";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { setMedicalData } from "@/store/slices/medical.slice";
import { RootState } from "@/store/store";
import { QuotePayload } from "@/types/quote/quote-data";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import handleError, { ErrorResponse } from "@/utils/handle-errors";
import { InfoBoxType, isMedicalEligibilityFlow } from "@/utils/medical.utils";
import { GatekeeperQuestion } from "@/utils/schema/gatekeeper-question-page-schema.utils";
import {
  hasPassedOrganiser,
  removeOrganiserPassedFlag,
} from "@/utils/session.utils";

import QMedicalContainer from "./q-medical-container";
import { MedicalQuestionType } from "./q-medical.types";

function QMedicalQuestionFormContent({
  qMedicalContent,
  newMedicalFlowEnabled,
  medicalConfirmationFlow,
  undiagnosedQuestionExperimentEnabled,
  routeName,
  application,
}: Readonly<{
  qMedicalContent: GatekeeperQuestion;
  newMedicalFlowEnabled: boolean;
  medicalConfirmationFlow?: MedicalConfirmationFlowEnum;
  undiagnosedQuestionExperimentEnabled?: boolean;
  routeName?: string;
  application?: "quote" | "renewal";
}>) {
  const {
    template,
    questionText,
    questionInfoText,
    ctaButtonText,
    yesCtaButtonText,
    noCtaButtonText,
    alertMessage,
    alertMessageOptimizelyTestEnabled,
    alertMessagePartner,
    nonRoutineTestInfoMessage,
    cancelEditingBtnText,
    finishEditingBtnText,
    removeMedicalConditionModalTitle,
    removeMedicalConditionModalDescription,
    removeMedicalConditionModalDescriptionRenewal,
    removeMedicalConditionModalCTABtnOne,
    removeMedicalConditionModalCTABtnTwo,
    saveQuestionModalTitle,
    saveQuestionModalDescription,
    saveQuestionModalCTABtnOne,
    saveQuestionModalCTABtnTwo,
    questionInfoTextWithUndiagnosed,
    questionUndiagnosedText,
    questionUndiagnosedInfoText,
    yesUndiagnosedCtaButtonText,
    noUndiagnosedCtaButtonText,
    alertMessageUndiagnosed,
    alertMessageUndiagnosedPartner,
    backToRenewalBtnText,
  } = qMedicalContent;

  const router = useRouter();
  const dispatch = useAppDispatch();
  const searchParams = useSearchParams();
  const editPage = searchParams.get(MedicalEdit);
  const travelerEdit = searchParams.get(TravellerEdit);
  const organizerEdit = searchParams.get(OrganizerEdit);
  const [updateGKAnswers, setUpdateGKAnswers] = useState(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [viewPage, setViewPage] = useState<string>("");
  const { current } = useWizard();
  const isMedicalNoAbTest =
    globalThis.window !== undefined &&
    APPLICATION === Quote &&
    PRODUCT === BRAND_STAYSURE &&
    localStorage.getItem(OptimizelyExperimentsEnum.MEDICAL_NO_TO_DASHBOARD) ===
      VARIANT_FLOW;
  if (isMedicalNoAbTest) {
    let noConditionsEditFlowNext: string | undefined;
    if (travelerEdit) {
      noConditionsEditFlowNext = current?.edges?.travellerEditNext ?? undefined;
    } else if (editPage) {
      noConditionsEditFlowNext = current?.edges?.editFlowNext ?? undefined;
    } else if (organizerEdit) {
      noConditionsEditFlowNext = current?.edges?.organizerEditNext ?? undefined;
    }
    current.edges = {
      ...current.edges,
      travllersWithNoMedicalConditionsEditFlowNext: noConditionsEditFlowNext,
      travellersWithNoMedicalConditionsNext: current.next ?? undefined,
    };
  }

  const [isNewMedicalConfirmationPage, setIsNewMedicalConfirmationPage] =
    useState(false);

  useEffect(() => {
    if (newMedicalFlowEnabled && routeName === QuestionMedicalConfirmation)
      setIsNewMedicalConfirmationPage(true);
  }, [newMedicalFlowEnabled, routeName]);

  if (
    application === RenewalPage &&
    routeName === QuestionMedicalConfirmation
  ) {
    sessionStorage.setItem("isMedicalAccordionOpen", "true");
  }

  const medicalDetails = useAppSelector((state: RootState) => state.medical);

  const quoteResponse = useAppSelector((state: RootState) => state.quote);
  const { version, referenceId, attributes } = quoteResponse;

  // Use partner message hook for UTM-based partner-specific messaging
  const { getPartnerMessage } = usePartnerMessage();

  const {
    gkData,
    gkLoading,
    gkError,
    updateTravelerDetails,
    updateTravelerDetailsInQuoteCreate,
    updateTravelersData,
  } = useSaveGateKeeperAnswers({
    version,
    referenceId,
    viewPage,
    patchData: medicalDetails,
    triggerUpdate: updateGKAnswers,
    resetTrigger: useCallback(() => setUpdateGKAnswers(false), []),
    experimentDetails: undiagnosedQuestionExperimentEnabled
      ? {
          undiagnosedFlow:
            medicalConfirmationFlow ??
            MedicalConfirmationFlowEnum.withoutUndiagnosedSymptomsQuestion,
        }
      : null,
  });

  const travellers = useMemo(() => {
    return attributes?.travellerDetails?.traveller || [];
  }, [attributes?.travellerDetails?.traveller]);

  const isIndividualAndOrganizerTravelling = useMemo(() => {
    return (
      attributes?.organiser?.travelling &&
      attributes?.tripDetails?.partyType === Individual
    );
  }, [attributes?.organiser?.travelling, attributes?.tripDetails?.partyType]);

  const getTravelerMedical = useCallback(() => {
    return (
      travellers?.some((traveller) => traveller.medical !== undefined) ||
      sessionStorage.getItem("isPrevMedicalAvailable") === "true"
    );
  }, [travellers]);

  const questionType = useMemo((): MedicalQuestionType | null => {
    const templateName = template ?? "unknown";

    switch (templateName) {
      case "quote:components/organisms/questions/medical/q-medical-confirmation-block":
      case "quote:components/organisms/questions/medical/medical-confirmation-block-new":
        setViewPage(Routes.MedicalConfirmation);
        return MedicalQuestionType.Confirmation;
      case "quote:components/organisms/questions/medical/q-medical-conditions-block":
        setViewPage(Routes.MedicalConditions);
        return MedicalQuestionType.Conditions;
      case "quote:components/organisms/questions/medical/q-medical-treatments-block":
      case "quote:components/organisms/questions/medical/medical-treatments-block-new":
        setViewPage(Routes.MedicalTreatments);
        return MedicalQuestionType.Treatments;
      case "quote:components/organisms/questions/medical/medical-eligibility-block-new":
        setViewPage(Routes.MedicalEligibility);
        return MedicalQuestionType.Eligibility;
      default:
        return null;
    }
  }, [template]);

  const getPathName = useCallback(() => {
    let pathName = current?.edges?.editFlowNext;

    if (travelerEdit) {
      pathName = current?.edges?.travellerEditNext;
    } else if (organizerEdit) {
      pathName = current?.edges?.organizerEditNext;
    }

    return pathName;
  }, [current, travelerEdit, organizerEdit]);

  useEffect(() => {
    const handleEffect = async () => {
      if (!gkLoading && gkData) {
        let path: string | undefined = current?.next ?? "";

        if (editPage || travelerEdit || organizerEdit) {
          if (
            questionType === MedicalQuestionType.Treatments &&
            medicalDetails.agreedConfirmation &&
            (medicalDetails.agreedHistory === false || newMedicalFlowEnabled) &&
            medicalDetails.agreedTreatments === false
          ) {
            // To handle both renewal and quote flow.
            path = current.edges?.travellersWithNoMedicalConditionsNext ?? "";
          }

          path = application === RenewalPage ? path : getPathName() ?? "";
        }

        if (
          newMedicalFlowEnabled &&
          medicalDetails.agreedConfirmation &&
          medicalDetails.agreedTreatments === false &&
          routeName !== QuestionMedicalConfirmation &&
          routeName !== QuestionMedicalEligibility
        ) {
          if (travelerEdit || editPage) {
            path =
              current.edges?.travllersWithNoMedicalConditionsEditFlowNext ?? "";
          } else {
            path = current.edges?.travellersWithNoMedicalConditionsNext ?? "";
          }
        }
        if (current.id === QuestionMedicalTreatments) {
          sessionStorage.removeItem("isPrevMedicalAvailable");

          // Clear organizer session flag when successfully completing medical treatments
          if (hasPassedOrganiser()) {
            removeOrganiserPassedFlag();
          }
          // Check if edit originated from quote-review page and user has NO medical data
          const editSection = getQuoteReviewSummaryEditSection();
          const hasNoMedicalData =
            medicalDetails.agreedTreatments === false &&
            (medicalDetails.agreedHistory === false || newMedicalFlowEnabled) &&
            sessionStorage.getItem("isPrevMedicalAvailable") !== "true";

          if (
            editSection &&
            (travelerEdit || organizerEdit) &&
            hasNoMedicalData
          ) {
            // Only navigate to quote-review if user has NO medical conditions/treatments
            // If they have medical data, continue to medical dashboard instead
            setButtonLoading(false);
            router.push(
              current.edges.cancelQuoteReviewSummaryEdit ?? "/quote-review"
            );
            return;
          }
        }
        router.push(path);
        setButtonLoading(false);
      } else if (!gkLoading && gkError !== null) {
        if (application === RenewalPage) {
          await handleError(
            gkError as ApiError<ErrorResponse>,
            router,
            dispatch
          );
          return;
        }
        const errorObject = await apiErrorMappings(gkError);
        errorObject.multiButton = true;
        dispatch(setApiError(errorObject));
        setButtonLoading(false);
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gkData,
    gkLoading,
    gkError,
    medicalDetails,
    editPage,
    travelerEdit,
    current,
    current.edges,
  ]);

  const { setSelectedState, setSelectedTwoState } = useGatekeeperQuestions();

  const questionValue = medicalDetails ? medicalDetails[questionType!] : null;
  const questionTwoValue =
    questionType === MedicalQuestionType.Confirmation && medicalDetails
      ? medicalDetails[MedicalQuestionType.Undiagnosed] ?? null
      : null;
  let selected = 0;
  let selectedTwo = 0;
  if (!isNewMedicalConfirmationPage) {
    if (typeof questionValue === "boolean") {
      selected = questionValue === true ? 1 : 2;
    }
  }
  if (
    isNewMedicalConfirmationPage ||
    routeName === QuestionMedicalEligibility
  ) {
    if (typeof questionValue === "boolean") {
      selected = questionValue === true ? 2 : 1;
    }
  }
  if (
    medicalConfirmationFlow ===
    MedicalConfirmationFlowEnum.withUndiagnosedSymptomsQuestion
  ) {
    if (typeof questionTwoValue === "boolean") {
      selectedTwo = questionTwoValue === true ? 1 : 2;
    }
  }

  useEffect(() => {
    setSelectedState(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);
  useEffect(() => {
    setSelectedTwoState(selectedTwo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTwo]);

  useEffect(() => {
    const eventHandler = (e: BeforeUnloadEvent) => {
      const confirmationMessage =
        "It looks like you have been editing something. " +
        "If you leave before saving, your changes will be lost.";

      if (e) {
        e.preventDefault();
        sessionStorage.removeItem("organiserTravellerId");
        (e || window.event).returnValue = confirmationMessage;
      }

      return confirmationMessage;
    };

    const isOnIOS =
      navigator.userAgent.match(/iPad/i) ||
      navigator.userAgent.match(/iPhone/i);
    const eventName = isOnIOS ? "pagehide" : "beforeunload";

    if (editPage || travelerEdit || organizerEdit) {
      window.addEventListener(eventName, eventHandler);
    }

    return () => {
      window.removeEventListener(eventName, eventHandler);
    };
  }, [editPage, travelerEdit, organizerEdit]);

  // This helps to unselect the GK answers
  const getAnswer = useCallback(
    (answerNo: number) => {
      const answer =
        isNewMedicalConfirmationPage || routeName === QuestionMedicalEligibility
          ? answerNo === 2
          : answerNo === 1;

      if (
        !((questionType as MedicalQuestionType) in medicalDetails) ||
        medicalDetails[questionType as MedicalQuestionType] !== answer
      ) {
        return answer;
      }
      return null;
    },
    [isNewMedicalConfirmationPage, medicalDetails, questionType, routeName]
  );

  const setAnswerNo = useCallback(
    (answerNo: number) => {
      const answer = getAnswer(answerNo);

      const medicalPayload = {
        ...medicalDetails,
        [questionType as string]: answer,
      };

      // set default undiagnosed value for Undiagnosed Symptoms controlled flow
      if (
        questionType === MedicalQuestionType.Confirmation &&
        medicalConfirmationFlow ===
          MedicalConfirmationFlowEnum.withoutUndiagnosedSymptomsQuestion
      ) {
        medicalPayload[MedicalQuestionType.Undiagnosed] = false;
      }

      dispatch(setMedicalData(medicalPayload));
    },
    [dispatch, getAnswer, medicalConfirmationFlow, medicalDetails, questionType]
  );

  const getQuestionTwoAnswer = useCallback(
    (answerNo: number) => {
      if (questionType === MedicalQuestionType.Confirmation) {
        // answer no 1 equals to Yes(true), reset selection if user selected the same option, else set user selection
        if ((answerNo === 1) !== medicalDetails.hasUndiagnosedSymptoms) {
          return answerNo === 1;
        }
      }
      return null;
    },
    [medicalDetails, questionType]
  );

  const setQuestionTwoAnswer = useCallback(
    (answerNo: number) => {
      const answer = getQuestionTwoAnswer(answerNo);
      dispatch(
        setMedicalData({
          ...medicalDetails,
          [MedicalQuestionType.Undiagnosed]: answer,
        })
      );
    },
    [dispatch, getQuestionTwoAnswer, medicalDetails]
  );

  const handleSubmitButton = useCallback(
    (usagePage: string) => {
      setButtonLoading(true);
      let payload = {
        ...quoteResponse,
        version,
      };

      // If user has no conditions or treatments, bypass the medical dashboard
      if (
        usagePage === QuestionMedicalTreatments &&
        medicalDetails.agreedConfirmation &&
        (medicalDetails.agreedHistory === false || newMedicalFlowEnabled) &&
        (medicalDetails.agreedTreatments === false || isMedicalNoAbTest)
      ) {
        if (!!getTravelerMedical() && quoteResponse) {
          payload = {
            ...quoteResponse,
            version,
            attributes: {
              ...quoteResponse.attributes,
              travellerDetails: {
                ...quoteResponse.attributes.travellerDetails,
                gateKeeperAnswers: medicalDetails,
              },
            },
          };
        }
      }

      if (
        (travelerEdit ||
          organizerEdit ||
          (editPage && application === RenewalPage)) &&
        usagePage === QuestionMedicalTreatments &&
        payload
      ) {
        // Traveler | Organizer edit flow last gatekeeper page handler
        updateTravelerDetails(
          {
            ...payload,
            viewPage: Routes.MedicalTreatments,
          } as unknown as QuotePayload,
          newMedicalFlowEnabled,
          (editPage && application === RenewalPage) as boolean
        );

        // Continue with normal traveler/organizer update flow
      } else if (editPage && usagePage === QuestionMedicalTreatments) {
        // Medical edit flow last gatekeeper page handler
        if (medicalDetails.agreedTreatments === false) {
          sessionStorage.removeItem("isPrevMedicalAvailable");
          router.push(
            current.edges?.travllersWithNoMedicalConditionsEditFlowNext ?? ""
          );
        }
        setUpdateGKAnswers(true);

        if (application !== RenewalPage) {
          updateTravelersData();
        }
      } else if (editPage || travelerEdit || organizerEdit) {
        // Traveler and Medical edit flow intermediate gatekeeper page handler
        setButtonLoading(false);
        router.push(getPathName() ?? "");
      } else {
        // Regular flow
        const isMedicalConditionSet =
          usagePage === QuestionMedicalTreatments &&
          medicalDetails.agreedConfirmation &&
          (medicalDetails.agreedHistory === false || newMedicalFlowEnabled) &&
          medicalDetails.agreedTreatments === false &&
          !!getTravelerMedical();

        if (isMedicalConditionSet) {
          // user added medical condition and then set GK answers
          updateTravelerDetailsInQuoteCreate();
        } else {
          setUpdateGKAnswers(true);
        }
      }
    },
    [
      quoteResponse,
      version,
      medicalDetails,
      newMedicalFlowEnabled,
      isMedicalNoAbTest,
      travelerEdit,
      organizerEdit,
      editPage,
      application,
      getTravelerMedical,
      updateTravelerDetails,
      router,
      current.edges?.travllersWithNoMedicalConditionsEditFlowNext,
      updateTravelersData,
      getPathName,
      updateTravelerDetailsInQuoteCreate,
    ]
  );

  const handleBackButton = useCallback(
    (usagePage: string) => {
      let path: string | undefined = current.prev ?? "";
      if (
        (usagePage === QuestionMedicalEligibility &&
          isMedicalEligibilityFlow()) ||
        (usagePage === QuestionMedicalConfirmation &&
          !isMedicalEligibilityFlow())
      ) {
        const questionOrderVariant = isContactPageShiftFlowExperimentOn();
        if (
          questionOrderVariant &&
          (editPage || travelerEdit || organizerEdit)
        ) {
          path = isIndividualAndOrganizerTravelling
            ? current.edges?.isIndividualAndOrganizerTravellingPrev
            : current.edges?.editFlowPrev;
        } else if (
          newMedicalFlowEnabled &&
          isIndividualAndOrganizerTravelling &&
          !questionOrderVariant
        ) {
          path = current.edges?.isIndividualAndOrganizerTravellingPrev;
        }
      }
      if (editPage) {
        path = `${path}/?${MedicalEdit}=true`;
      } else if (travelerEdit) {
        path = `${path}/?${TravellerEdit}=true`;
      } else if (organizerEdit) {
        path = `${path}/?${OrganizerEdit}=true`;
      }
      router.push(path ?? "");
    },
    [
      current.prev,
      current.edges?.isIndividualAndOrganizerTravellingPrev,
      current.edges?.editFlowPrev,
      editPage,
      travelerEdit,
      organizerEdit,
      router,
      newMedicalFlowEnabled,
      isIndividualAndOrganizerTravelling,
    ]
  );
  const shouldShowAlert = () => {
    if (
      questionType === MedicalQuestionType.Eligibility &&
      medicalDetails?.agreedEligibility === false
    ) {
      return true;
    }

    if (
      questionType === MedicalQuestionType.Confirmation &&
      medicalDetails?.agreedConfirmation === false
    ) {
      return true;
    }

    if (
      questionType === MedicalQuestionType.Treatments &&
      (medicalDetails?.agreedHistory === false || newMedicalFlowEnabled) &&
      medicalDetails?.agreedTreatments === false
    ) {
      return true;
    }

    if (
      questionType === MedicalQuestionType.Confirmation &&
      medicalDetails?.agreedConfirmation === true &&
      medicalDetails?.hasUndiagnosedSymptoms === true
    ) {
      return true;
    }

    return false;
  };

  const getAlertMessage = () => {
    if (
      questionType === MedicalQuestionType.Confirmation &&
      medicalDetails?.agreedConfirmation === true &&
      medicalDetails?.hasUndiagnosedSymptoms === true
    ) {
      return getPartnerMessage(
        alertMessageUndiagnosed!,
        alertMessageUndiagnosedPartner,
        "alertMessageUndiagnosedPartner"
      );
    }

    if (
      questionType === MedicalQuestionType.Confirmation &&
      medicalDetails?.agreedConfirmation === false &&
      alertMessageOptimizelyTestEnabled &&
      isMedicalEligibilityFlow()
    ) {
      return alertMessageOptimizelyTestEnabled;
    }

    return getPartnerMessage(
      alertMessage!,
      alertMessagePartner,
      "alertMessagePartner"
    );
  };

  const getInfoBoxType = (): InfoBoxType => {
    if (
      routeName === QuestionMedicalConfirmation &&
      medicalDetails?.agreedConfirmation
    ) {
      return "info";
    }

    const infoBoxType = current?.uiElementControls?.infoBoxType;

    return typeof infoBoxType === "string"
      ? (infoBoxType as InfoBoxType)
      : "info";
  };

  const getInfoBoxClassesByType = (type?: InfoBoxType): string => {
    switch (type) {
      case "critical":
        return "!border-border-critical-primary bg-surface-critical";

      case "warning":
        return "!border-border-warning-primary bg-surface-warning";

      case "info":
      default:
        return "!border-border-info-primary bg-surface-info-2";
    }
  };

  const renderAlertMessage = () => {
    if (!shouldShowAlert()) {
      return null;
    }

    const infoBoxType = getInfoBoxType();

    const htmlContent = getAlertMessage();

    const message = parse(htmlContent, {
      replace: (domNode) => {
        if (
          domNode instanceof Element &&
          domNode.name === "a" &&
          routeName === QuestionMedicalEligibility
        ) {
          return (
            <a
              href={domNode.attribs.href}
              target={domNode.attribs.target}
              rel={domNode.attribs.rel}
              className="inline text-secondary-inactive-hover underline leading-[24px] text-2xs font-semibold break-words"
            >
              {domToReact(domNode.children as DOMNode[])}
            </a>
          );
        }
      },
    });

    return (
      <InlineInfoBox
        id="question-details-info-box-id"
        className={`mt-3 border-l-8 rounded-md ${getInfoBoxClassesByType(
          infoBoxType
        )} text-surface-neutral-option-10 py-2 px-4`}
        content={message}
      />
    );
  };

  const renderQuestion = () => {
    const errorEl = <div>Cannot find question</div>;
    if (!questionType) {
      return errorEl;
    }

    let usagePage;
    switch (questionType) {
      case MedicalQuestionType.Confirmation:
        usagePage = QuestionMedicalConfirmation;
        break;
      case MedicalQuestionType.Conditions:
        usagePage = QuestionMedicalConditions;
        break;
      case MedicalQuestionType.Treatments:
        usagePage = QuestionMedicalTreatments;
        break;
      case MedicalQuestionType.Eligibility:
        usagePage = QuestionMedicalEligibility;
        break;
      default:
        usagePage = null;
    }

    if (!usagePage) {
      return errorEl;
    }

    const showInforBox = () => {
      setTimeout(() => {
        const element = document.getElementById("question-details-info-box-id");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    };

    return (
      <QMedicalContainer
        showInforBox={showInforBox}
        questionType={questionType}
        questionText={questionText}
        questionInfoText={questionInfoText}
        ctaButtonText={ctaButtonText ?? ""}
        cancelEditingBtnText={cancelEditingBtnText}
        usagePage={usagePage}
        selectedAnswer={selected}
        questionTwoSelectedAnswer={selectedTwo}
        setSelectedAnswer={setAnswerNo}
        setQuestionTwoAnswer={setQuestionTwoAnswer}
        onSubmit={handleSubmitButton}
        onBack={handleBackButton}
        yesCtaButtonText={yesCtaButtonText}
        noCtaButtonText={noCtaButtonText}
        ctnBtnLoading={buttonLoading}
        isTravelerHasMedicalCondition={!!getTravelerMedical()}
        isFinishEditing={
          !!(
            usagePage === QuestionMedicalTreatments &&
            (editPage || travelerEdit) &&
            medicalDetails.agreedConfirmation &&
            (medicalDetails.agreedHistory === false || newMedicalFlowEnabled) &&
            medicalDetails.agreedTreatments === false
          )
        }
        removeMedicalConditionModalTitle={removeMedicalConditionModalTitle}
        removeMedicalConditionModalDescription={
          removeMedicalConditionModalDescription
        }
        removeMedicalConditionModalDescriptionRenewal={
          removeMedicalConditionModalDescriptionRenewal
        }
        removeMedicalConditionModalCTABtnOne={
          removeMedicalConditionModalCTABtnOne
        }
        removeMedicalConditionModalCTABtnTwo={
          removeMedicalConditionModalCTABtnTwo
        }
        saveQuestionModalTitle={saveQuestionModalTitle}
        saveQuestionModalDescription={saveQuestionModalDescription}
        saveQuestionModalCTABtnOne={saveQuestionModalCTABtnOne}
        saveQuestionModalCTABtnTwo={saveQuestionModalCTABtnTwo}
        finishEditingBtnText={finishEditingBtnText}
        medicalConfirmationPageContent={isNewMedicalConfirmationPage}
        newMedicalFlowEnabled={newMedicalFlowEnabled}
        medicalConfirmationFlow={medicalConfirmationFlow}
        questionInfoTextWithUndiagnosed={questionInfoTextWithUndiagnosed}
        questionInfoTextWithUndiagnosedOptimizelyTestEnabled={
          qMedicalContent.questionInfoTextWithUndiagnosedOptimizelyTestEnabled
        }
        questionUndiagnosedText={questionUndiagnosedText}
        questionUndiagnosedInfoText={questionUndiagnosedInfoText}
        yesUndiagnosedCtaButtonText={yesUndiagnosedCtaButtonText}
        noUndiagnosedCtaButtonText={noUndiagnosedCtaButtonText}
        application={application}
        backToRenewalBtnText={backToRenewalBtnText}
        nonRoutineTestInfoMessage={nonRoutineTestInfoMessage}
      >
        {alertMessage && renderAlertMessage()}
      </QMedicalContainer>
    );
  };
  return <Suspense>{renderQuestion()}</Suspense>;
}

export default withUndiagnosedSymptoms(
  withMedicalFlow(withApplication(QMedicalQuestionFormContent))
);
